import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import api from "../../api/axiosInstance";
import { DeliveryModelCell, ServiceTypesCell, formatDate, formatDuration, formatPersonName } from "./opportunityDisplay";
import OpportunityDetailsDialog from "./OpportunityDetailsDialog";
import OpportunityFormModal from "./OpportunityFormModal";

// Table head row as on Manage Invites: 56px, grey fill, rounded top corners.
const HEAD_ROW_SX = {
  height: 56,
  "& th": {
    backgroundColor: "#EAE7E6",
    borderBottom: "1px solid #EAE7E6",
    py: 0,
    fontWeight: 500,
    color: "rgba(0, 0, 0, 0.87)",
  },
  "& th:first-of-type": { borderTopLeftRadius: "8px" },
  "& th:last-of-type": { borderTopRightRadius: "8px" },
};

const AdminManageOpportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewing, setViewing] = useState(null);
  // { opportunity: null } = create; { opportunity } = edit; null = closed.
  const [formFor, setFormFor] = useState(null);

  const fetchOpportunities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get("/opportunities/");
      setOpportunities(data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to fetch opportunities");
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  // A new opportunity appears first, so a create goes back to page 1; an edit stays on its page.
  const handleSaved = () => {
    if (!formFor?.opportunity) setPage(0);
    setFormFor(null);
    fetchOpportunities();
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const paginatedOpportunities = opportunities.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalOpportunities = opportunities.length;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h4" fontWeight={700} sx={{ fontSize: "1.5rem" }}>
          Manage Opportunities
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
          overflow: "hidden",
          backgroundColor: "#fff",
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Opportunities Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalOpportunities} opportunities in the system
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setFormFor({ opportunity: null })}
            sx={{
              backgroundColor: "#E65100",
              "&:hover": { backgroundColor: "#BF360C" },
            }}
          >
            CREATE OPPORTUNITY
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 2 }}>{error}</Typography>
        ) : (
        <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={HEAD_ROW_SX}>
                <TableCell>Opportunity</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Delivery Model</TableCell>
                <TableCell>Service Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Lead</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOpportunities.map((opp) => (
                <TableRow
                  key={opp._id}
                  sx={{
                    "&:hover": { backgroundColor: "action.hover" },
                    "& td": {
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    },
                  }}
                >
                  <TableCell>{opp.name || "—"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDuration(opp)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <DeliveryModelCell opportunity={opp} />
                  </TableCell>
                  <TableCell>
                    <ServiceTypesCell opportunity={opp} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{opp.status || "—"}</TableCell>
                  <TableCell>{formatPersonName(opp.lead)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(opp.dueDate)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <IconButton
                      size="small"
                      aria-label={`View ${opp.opportunityId || "opportunity"}`}
                      onClick={() => setViewing(opp)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        mr: 1,
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Edit ${opp.opportunityId || "opportunity"}`}
                      onClick={() => setFormFor({ opportunity: opp })}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalOpportunities}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
          }}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count}`
          }
          labelRowsPerPage="Rows per page:"
        />
        </>
        )}
      </Paper>

      <OpportunityDetailsDialog opportunity={viewing} onClose={() => setViewing(null)} />
      <OpportunityFormModal
        open={Boolean(formFor)}
        opportunity={formFor?.opportunity || null}
        onClose={() => setFormFor(null)}
        onSaved={handleSaved}
      />
    </Box>
  );
};

export default AdminManageOpportunities;
